import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatMenu } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'src/app/shared/services/oauth.service';
import { DebuggerService } from 'src/app/shared/services/debugger.service';
import { ProjectService } from 'src/app/Modules/Projects/project.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { Project } from 'src/app/shared/Models/project-model';
import { PermissionConstants as Permissions } from 'src/app/constants/permissions';
import { AnnouncementNotificationService } from 'src/app/Modules/announcements/services/announcement-notification.service';
import {
  selectCurrentProjectId,
  selectCurrentUser,
  selectIsLoading,
  selectSelectableProjects,
} from 'src/app/state/app.selectors';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() userMenu!: MatMenu;
  @Input() themeMenu!: MatMenu;
  @Input() langMenu!: MatMenu;
  @Input() selectedTheme: 'm3-light' | 'm3-dark' = 'm3-light';
  @Input() langIcon: string = 'fi-fi';

  @Output() menuToggle = new EventEmitter<void>();

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.Handset, Breakpoints.Small])
    .pipe(map(result => result.matches), shareReplay());

  loginState$ = this.authService.loginChanged;
  /** Drives the debug dot on the avatar; the switch itself is in the user menu. */
  debugMode$ = this.debugService.debugMode$;
  /**
   * Owned app-wide, so the badge is correct on every route — not just on home. Counts
   * everything needing attention (unread, or read but awaiting a required
   * acknowledgment) regardless of priority — the banner and blocking dialog are the
   * priority-specific nudges layered on top of this same badge.
   */
  attentionCount$ = this.announcementNotifications.attentionCount$;
  hasAttention$ = this.attentionCount$.pipe(map(count => count > 0));
  /**
   * Read by assistive tech alongside the visual badge (`matBadgeDescription`) — empty
   * string when there is nothing to announce, so no description is exposed at all.
   */
  bellBadgeDescription$ = this.attentionCount$.pipe(
    map(count => count > 0
      ? this.translate.instant('ANNOUNCEMENTS.BELL_BADGE_DESCRIPTION', { count })
      : '')
  );
  loginReady = false;
  username = '';
  projects: Project[] = [];
  projectsLoading$ = this.store.select(selectIsLoading);
  selectedProject$ = this.store.select(selectCurrentProjectId);
  permissions = Permissions;

  navLinks = [
    { path: '/', label: 'NAVBAR.HOME', icon: 'home' },
  ];

  private subs = new Subscription();

  constructor(
    private breakpointObserver: BreakpointObserver,
    private authService: OAuthService,
    private store: Store,
    private debugService: DebuggerService,
    private projectService: ProjectService,
    private translate: TranslateService,
    private snackService: SnackBarService,
    private announcementNotifications: AnnouncementNotificationService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.store.select(selectCurrentUser).subscribe(user => {
        this.username = user?.profile.preferred_username ?? '';
      })
    );
    this.subs.add(
      this.store.select(selectSelectableProjects).subscribe(projects => {
        this.projects = projects;
      })
    );
    this.subs.add(
      this.loginState$.pipe(
        tap(() => { this.loginReady = true; })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    this.authService.logout();
  }

  changeProject(projectId: number): void {
    this.projectService.updateUserProject(projectId).subscribe(() => {
      this.snackService.openSnackBar(
        this.translate.instant('PROJECTCHANGEDMESSAGE'), 'OK', 5000
      );
    });
  }
}
